/**
 * Flow task visual state for the minimap (and any code that needs readiness semantics).
 * Main canvas node body fill does not use isActive / isAvailable — only locked / excluded
 * tints (see useTaskNodeStyles + states.css).
 */

import {
  isStatusCompleted,
  isStatusInProgress,
  isStatusNotStarted,
} from './statusUtils';
import {
  isNestedSprintCompleted,
  isNestedSprintInProgress,
  isTaskBlocked,
} from '../../shared/utils/taskAvailabilityUtils';

/**
 * @param {Object} task
 * @param {Array} statuses
 * @param {Object|null} lockState
 * @param {Object|null} nestedSprint - nested sprint for this task, if any
 * @param {Array} allSprints - all campaigns (for nested-in-nested actionable checks)
 */
export function deriveFlowTaskVisualState(
  task,
  statuses = [],
  lockState = null,
  nestedSprint = null,
  allSprints = [],
) {
  const lockInfo = lockState || {};
  const isExcluded = !!lockInfo.isExcluded;

  const nestedSprints = nestedSprint ? [nestedSprint] : [];

  const isComplete =
    task.nodeType === 'decision'
      ? task.answer != null
      : isNestedSprintCompleted(task, nestedSprints) || isStatusCompleted(task.status, statuses);

  const isLocked = !!lockInfo.isLocked && !isComplete;

  const isBlocked = !isComplete && isTaskBlocked(task, nestedSprints, statuses, allSprints);

  const isActive =
    !isComplete &&
    !isBlocked &&
    (isNestedSprintInProgress(task, nestedSprints, statuses, allSprints) ||
      isStatusInProgress(task.status, statuses));

  const isAvailable =
    !isComplete &&
    !isActive &&
    !isBlocked &&
    !isExcluded &&
    !lockInfo.isLocked &&
    isStatusNotStarted(task.status, statuses) &&
    !(task.nodeType === 'decision' && task.answer);

  return {
    isComplete,
    isActive,
    isAvailable,
    isLocked,
    isExcluded,
    isBlocked,
  };
}
